import { Button, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import { notificationsVariants } from "../constants/notifications-variants.tsx"
import { isNotificationClickable } from "../helpers/isNotificationClickable.ts"
import type { Notification } from "../types/notification.types.ts"

const { Text, Paragraph } = Typography

interface NotificationsListCardProps {
  notification: Notification
  onMarkAsRead: (id: number) => void
  onCardClick: (notification: Notification) => void
  showDelete?: boolean
  handleDelete: (notificationId: number | null) => void
}

export function NotificationsListCard({
  notification,
  onMarkAsRead,
  onCardClick,
  showDelete,
  handleDelete
}: NotificationsListCardProps) {
  const { t } = useTranslation()
  const config =
    notificationsVariants?.[notification?.level] || notificationsVariants.info

  const isCritical = notification?.level === 4
  const clickable = isNotificationClickable(notification)

  return (
    <Flex
      vertical
      gap="small"
      className={cn(
        "p-1.5 border rounded-lg",
        clickable &&
          "cursor-pointer hover:shadow-md hover:border-primary/30 transition-all duration-200",
        notification?.unread
          ? isCritical
            ? "bg-red-300/5 border-red-700/40"
            : "bg-primary/5 border-primary/40"
          : "bg-transparent border-border/50"
      )}
      onClick={clickable ? () => onCardClick(notification) : undefined}
    >
      <Flex gap={10} align="flex-start">
        <Flex
          align="center"
          justify="center"
          className="size-10 rounded-lg shrink-0"
          style={{
            backgroundColor: config.bg,
            color: config.color
          }}
        >
          {config.icon}
        </Flex>

        <div className="flex-1 min-w-0">
          <Text strong={notification?.unread} className="block leading-tight">
            {notification?.actor?.name || t("notifications.bell.noAuthor")}
          </Text>
          <Text type="secondary" className="text-xs">
            {notification?.verb}
          </Text>
        </div>

        {notification?.unread && (
          <span className="size-2 rounded-full bg-primary shrink-0 mt-1" />
        )}
      </Flex>

      <Paragraph
        className="mb-0"
        type="secondary"
        ellipsis={{ rows: 2, tooltip: notification.description }}
      >
        {notification?.description}
      </Paragraph>

      <Flex
        justify="space-between"
        align="center"
        className="pt-1 border-t border-border/30"
      >
        <Text type="secondary" className="text-xs">
          {t("notifications.bell.timeAgo", { time: notification?.timesince })}
        </Text>

        <Flex gap={5}>
          {notification?.unread && (
            <Button
              type="link"
              size="small"
              className="p-0 h-auto text-xs"
              onClick={(e) => {
                e.stopPropagation()
                onMarkAsRead(notification?.id)
              }}
            >
              {t("notifications.bell.markRead")}
            </Button>
          )}
          {showDelete && (
            <Button
              type="link"
              size="small"
              className="p-0 h-auto text-xs text-red-500"
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(notification?.id)
              }}
            >
              {t("notifications.bell.delete")}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}
